// =============================================================================
// FILE: packages/renderer/src/components/community/BrandConsentModal.tsx
// PURPOSE: A partner representative identifies themselves, attests they have
//   signing authority, chooses Approve/Decline, optionally provides their logo
//   (upload OR URL), and e-signs. Submission creates a durable evidence record.
//   Approved logos stay hidden until the Founder confirms in the Command Centre.
// =============================================================================

import { useState, type JSX, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { ShieldCheck, X, UploadCloud, Loader2, CheckCircle2 } from 'lucide-react';

const BACKEND: string = (() => {
  const w = (typeof window !== 'undefined'
    ? (window as unknown as Record<string, unknown>).__BACKEND_URL__
    : undefined) as string | undefined;
  return w || ((import.meta.env.VITE_BACKEND_URL as string) ?? '');
})();

interface Props { org: string; orgSlug: string; onClose: () => void; onSubmitted?: () => void; }

export function BrandConsentModal({ org, orgSlug, onClose, onSubmitted }: Props): JSX.Element {
  const [repName, setRepName] = useState('');
  const [repRole, setRepRole] = useState('');
  const [repEmail, setRepEmail] = useState('');
  const [decision, setDecision] = useState<'approve' | 'decline'>('approve');
  const [authority, setAuthority] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [signature, setSignature] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<string | null>(null);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError('');
    if (!repName || !repRole || !repEmail || !signature) { setError('Please complete all required fields, including your e-signature.'); return; }
    if (decision === 'approve' && !authority) { setError('You must confirm you have signing authority to approve logo use.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('org', org);
      fd.append('org_slug', orgSlug);
      fd.append('rep_name', repName);
      fd.append('rep_role', repRole);
      fd.append('rep_email', repEmail);
      fd.append('decision', decision);
      fd.append('authority', String(authority));
      fd.append('signature', signature);
      if (decision === 'approve') {
        if (logoUrl.trim()) fd.append('logo_url', logoUrl.trim());
        if (logoFile) fd.append('logo', logoFile);
      }
      const res = await fetch(`${BACKEND}/api/partners/consent`, { method: 'POST', body: fd });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Submission failed. Please try again.'); return; }
      setDone(data.message || 'Thank you — your response has been recorded.');
      onSubmitted?.();
    } catch {
      setError('Network error — please try again.');
    } finally {
      setBusy(false);
    }
  };

  const field = { width: '100%', padding: '9px 11px', borderRadius: 9, background: 'rgba(8,18,28,0.7)', border: '1px solid rgba(148,163,184,0.25)', color: '#e2e8f0', fontSize: 13 } as const;
  const labelStyle = { fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(148,172,210,0.7)', marginBottom: 4, display: 'block' } as const;

  return createPortal(
    <div data-testid="brand-consent-modal" onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(4,7,13,0.85)', backdropFilter: 'blur(6px)', display: 'grid', placeItems: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(560px, 100%)', maxHeight: '92vh', overflowY: 'auto', borderRadius: 16, background: 'linear-gradient(180deg,#0b1622,#070d16)', border: '1px solid rgba(37,181,168,0.28)', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 18px', borderBottom: '1px solid rgba(63,63,70,0.5)' }}>
          <ShieldCheck style={{ width: 20, height: 20, color: '#5eead4' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#e6fffb' }}>Brand Consent · {org}</div>
            <div style={{ fontSize: 11, color: 'rgba(148,172,210,0.6)' }}>Authorize display of your organization's logo on RCD² Engine</div>
          </div>
          <button data-testid="brand-consent-close" onClick={onClose} style={{ background: 'none', border: 'none', color: '#94acd2', cursor: 'pointer' }}><X style={{ width: 18, height: 18 }} /></button>
        </div>

        {done ? (
          <div data-testid="brand-consent-success" style={{ padding: 28, textAlign: 'center' }}>
            <CheckCircle2 style={{ width: 44, height: 44, color: '#5eead4', margin: '0 auto 12px' }} />
            <p style={{ fontSize: 14, color: '#e2e8f0', lineHeight: 1.5 }}>{done}</p>
            <button data-testid="brand-consent-done" onClick={onClose}
              style={{ marginTop: 18, padding: '9px 22px', borderRadius: 9, background: 'rgba(37,181,168,0.15)', border: '1px solid rgba(37,181,168,0.5)', color: '#5eead4', fontWeight: 700, cursor: 'pointer' }}>Close</button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 13 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div><label style={labelStyle}>Your name *</label><input data-testid="bc-name" style={field} value={repName} onChange={(e) => setRepName(e.target.value)} placeholder="Jane Doe" /></div>
              <div><label style={labelStyle}>Your role / title *</label><input data-testid="bc-role" style={field} value={repRole} onChange={(e) => setRepRole(e.target.value)} placeholder="Communications Director" /></div>
            </div>
            <div><label style={labelStyle}>Work email *</label><input data-testid="bc-email" style={field} type="email" value={repEmail} onChange={(e) => setRepEmail(e.target.value)} placeholder="jane@organization.org" /></div>

            <div>
              <label style={labelStyle}>Decision *</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['approve', 'decline'] as const).map((d) => (
                  <button key={d} type="button" data-testid={`bc-decision-${d}`} onClick={() => setDecision(d)}
                    style={{ flex: 1, padding: '9px', borderRadius: 9, fontSize: 12, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize',
                      background: decision === d ? (d === 'approve' ? 'rgba(37,181,168,0.18)' : 'rgba(248,113,113,0.15)') : 'rgba(8,18,28,0.6)',
                      border: `1px solid ${decision === d ? (d === 'approve' ? 'rgba(37,181,168,0.6)' : 'rgba(248,113,113,0.5)') : 'rgba(148,163,184,0.2)'}`,
                      color: decision === d ? (d === 'approve' ? '#5eead4' : '#fca5a5') : '#94acd2' }}>
                    {d === 'approve' ? 'Approve logo use' : 'Decline'}
                  </button>
                ))}
              </div>
            </div>

            {decision === 'approve' && (
              <>
                <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 12, color: '#cbd5e1', cursor: 'pointer', lineHeight: 1.4 }}>
                  <input data-testid="bc-authority" type="checkbox" checked={authority} onChange={(e) => setAuthority(e.target.checked)} style={{ marginTop: 2, accentColor: '#2db5a8' }} />
                  <span>I confirm I have the <strong>signing authority</strong> to authorize use of {org}'s brand mark by RCD² Engine.</span>
                </label>

                <div>
                  <label style={labelStyle}>Logo — upload a file</label>
                  <label data-testid="bc-logo-drop" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px', borderRadius: 9, border: '1px dashed rgba(37,181,168,0.4)', background: 'rgba(37,181,168,0.05)', cursor: 'pointer', color: '#94acd2', fontSize: 12 }}>
                    <UploadCloud style={{ width: 18, height: 18, color: '#5eead4' }} />
                    {logoFile ? <span style={{ color: '#5eead4' }}>{logoFile.name}</span> : <span>PNG, JPG, WEBP, GIF or SVG (max 3 MB)</span>}
                    <input data-testid="bc-logo-file" type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif" style={{ display: 'none' }} onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)} />
                  </label>
                  <div style={{ textAlign: 'center', fontSize: 10, color: 'rgba(148,172,210,0.5)', margin: '7px 0' }}>— or —</div>
                  <label style={labelStyle}>Paste an official logo URL</label>
                  <input data-testid="bc-logo-url" style={field} value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://organization.org/logo.png" />
                </div>
              </>
            )}

            <div><label style={labelStyle}>E-signature (type your full name) *</label><input data-testid="bc-signature" style={{ ...field, fontFamily: 'cursive', fontSize: 16 }} value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Jane Doe" /></div>

            {error && <div data-testid="bc-error" style={{ fontSize: 12, color: '#fca5a5', background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 8, padding: '8px 11px' }}>{error}</div>}

            <p style={{ fontSize: 9.5, color: 'rgba(148,172,210,0.45)', lineHeight: 1.4 }}>
              This creates a signed evidence record (with timestamp and network details). Approved logos are shown publicly
              only after the RCD² founder verifies this consent — nothing goes live automatically.
            </p>

            <button data-testid="bc-submit" type="submit" disabled={busy}
              style={{ padding: '11px', borderRadius: 10, background: '#2db5a8', border: 'none', color: '#04120f', fontWeight: 800, fontSize: 13, cursor: busy ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: busy ? 0.7 : 1 }}>
              {busy && <Loader2 className="animate-spin" style={{ width: 15, height: 15 }} />}
              {busy ? 'Submitting…' : 'Submit consent'}
            </button>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
